import {
  displayItemEnglishTitle,
  type PictureTalkSceneDetail,
  type PictureTalkSceneItem,
} from '@/modules/learning/api/pictureTalk';
import { PICTURE_TALK_STANDARD_DIALECT_KEY } from '@/modules/picture-talk/live/pictureTalkDialectKeys';
import {
  resolvePictureTalkPrompt,
  type ResolvePictureTalkPromptResponse,
} from '@/modules/tutor/api/promptLibraryAdminService';

export const PICTURE_TALK_EMPTY_FINAL_PROMPT = 'PICTURE_TALK_EMPTY_FINAL_PROMPT';

export function buildPictureTalkResolverSceneContext(scene: PictureTalkSceneDetail): string {
  return `${scene.scene_title_ta || scene.scene_title_en || scene.title || ''} - ${scene.scene_summary_ta || scene.scene_summary_en || 'educational picture for Tamil conversation practice'}`;
}

/** Human-readable label for resolver `selected_item` when the student pre-selected an object. */
export function buildPictureTalkResolverSelectedItem(item?: PictureTalkSceneItem | null): string | undefined {
  if (!item) return undefined;
  const label = displayItemEnglishTitle(item).trim();
  return label || undefined;
}

/**
 * Loads Picture Talk LIVE system instructions from the prompt-library resolver only.
 * Call before microphone / Gemini live connect so the resolver appears in Network as soon as the user starts.
 */
export async function resolvePictureTalkLiveSystemInstruction(options: {
  scene: PictureTalkSceneDetail;
  dialectKey: string;
  studentGrade?: number | string | null;
  selectedItem?: PictureTalkSceneItem | null;
}): Promise<{ systemInstruction: string; promptRes: ResolvePictureTalkPromptResponse }> {
  const { scene, dialectKey, studentGrade, selectedItem } = options;
  const gradeNum =
    studentGrade != null && studentGrade !== ''
      ? Number(studentGrade)
      : undefined;
  const grade = gradeNum != null && Number.isFinite(gradeNum) ? gradeNum : undefined;

  const selectedItemLabel = buildPictureTalkResolverSelectedItem(selectedItem);
  const promptRes = await resolvePictureTalkPrompt({
    grade,
    dialect_key: dialectKey.trim() || PICTURE_TALK_STANDARD_DIALECT_KEY,
    mode: 'LIVE',
    event_type: 'start',
    scene_context: buildPictureTalkResolverSceneContext(scene),
    ...(selectedItemLabel ? { selected_item: selectedItemLabel } : {}),
  });

  const trimmed = promptRes.final_prompt.trim();
  if (!trimmed) {
    const err = new Error(PICTURE_TALK_EMPTY_FINAL_PROMPT);
    err.name = 'PictureTalkEmptyFinalPromptError';
    throw err;
  }

  return { systemInstruction: trimmed, promptRes };
}
